/**
 * The desk's tab vocabulary, read off the URL (D-02, DESK-01, UI-SPEC §B).
 *
 * `?tab=` and `?page=` are curator-controlled text that select a database
 * predicate, so they are mapped through a closed vocabulary with a fallback
 * (the Applications `readSource` / `readStatus` pattern): nothing off the URL
 * is ever passed through. An unknown tab is Ready; a bad page is 1.
 *
 * The URL values are exactly the `desk_queue.desk_tab` values of 0035
 * (mobile/supabase/migrations/20240101000035_desk.sql), so the reader filters
 * `.eq('desk_tab', tab)` with no translation step. Tab order is the UI-SPEC's.
 *
 * Pure: no React, no Supabase, no translation lookup.
 */

export const DESK_TABS = ['ready', 'needs_a_look', 'discard', 'updates'] as const

export type DeskTab = (typeof DESK_TABS)[number]

export const DEFAULT_DESK_TAB: DeskTab = 'ready'

/** Rows per page (UI-SPEC §B, as on Applications and Users). */
export const DESK_PAGE_SIZE = 20

export const DESK_TAB_LABEL_KEYS: Record<DeskTab, string> = {
  ready: 'desk.tabs.ready',
  needs_a_look: 'desk.tabs.needs_a_look',
  discard: 'desk.tabs.discard',
  updates: 'desk.tabs.updates'
}

export const readDeskTab = (value: string | null): DeskTab =>
  DESK_TABS.find((allowed) => allowed === value) ?? DEFAULT_DESK_TAB

/** A positive integer page off the URL, else 1 ('1.5', '3x', '0', '-2' are all 1). */
export const readPage = (value: string | null): number => {
  if (value === null || !/^\d+$/.test(value)) return 1
  const page = Number(value)
  return page >= 1 ? page : 1
}

/**
 * The Discard pile "Why" column (`desk_queue.discard_kind` -> key). The reason
 * (`desk_queue.discard_reason`) is the `{{reason}}` value: for `rejected` the
 * caller translates it through `desk.reject.reasons.*`; for `not_event` it is the
 * editor's text, shown as is; `auto_rejected` (today only `elapsed`) needs none.
 */
export const DISCARD_REASON_KEYS = {
  auto_rejected: 'desk.discard.why_elapsed',
  rejected: 'desk.discard.why_rejected',
  not_event: { withReason: 'desk.discard.why_not_event', noReason: 'desk.discard.why_not_event_no_reason' }
} as const

export type DiscardKind = keyof typeof DISCARD_REASON_KEYS

export type DiscardWhy = { key: string; reason: string | null }

/** The Why key and its reason for one discard row; null for a kind outside the vocabulary. */
export const discardWhy = (kind: string | null, reason: string | null): DiscardWhy | null => {
  const text = reason?.trim() ? reason.trim() : null
  if (kind === 'auto_rejected') return { key: DISCARD_REASON_KEYS.auto_rejected, reason: null }
  if (kind === 'rejected') return { key: DISCARD_REASON_KEYS.rejected, reason: text }
  if (kind === 'not_event') {
    return text
      ? { key: DISCARD_REASON_KEYS.not_event.withReason, reason: text }
      : { key: DISCARD_REASON_KEYS.not_event.noReason, reason: null }
  }
  return null
}
